import { defineStore } from "pinia"
import { ref, type Ref } from "vue"
import { cloneDeep } from "es-toolkit"
import { timestamp } from "~/utils/datetime"
import updateProfile from "~/utils/update/updateProfile"
import { useCurrentAttackerStore } from "@/stores/currentAttacker"
import { useCurrentDefenderStore } from "@/stores/currentDefender"
import { useGlobalStore } from "@/stores/global"
import { useAttackContextStore } from "@/stores/attackContext"

export const useUpdateManagementStore = defineStore(
  "updateManagement",
  () => {
    // State
    const lastSeen: Ref<number | null> = ref(null)
    const updateInProgress: Ref<boolean> = ref(false)
    const versionToUpdateFrom: Ref<string | null> = ref(null)

    // Actions
    function checkDataIntegrity() {
      console.log("Checking data integrity...")
      const globalStore = useGlobalStore()

      if (typeof globalStore.profiles.attackers !== "undefined") {
        console.log("Legacy profiles architecture detected.")
        migrateProfiles()
      }

      const hasId = (profile) => {
        if (
          typeof profile.id === "undefined" ||
          typeof profile.id === "number"
        ) {
          return false
        }
        return profile.id !== "new"
      }

      let profileIdIssueDetected = false

      if (globalStore.profiles.length) {
        if (!globalStore.profiles.every(hasId)) {
          profileIdIssueDetected = true
        }
      }

      if (profileIdIssueDetected) {
        console.log("Profile IDs need to be updated...")
        globalStore.assignProfileIds()
      }
    }

    function migrateProfiles() {
      console.log("Migrating profiles...")
      const globalStore = useGlobalStore()

      // Deselect current attacker/defender profiles.
      const currentAttackerStore = useCurrentAttackerStore()
      const currentDefenderStore = useCurrentDefenderStore()
      currentAttackerStore.setCurrentAttacker(null)
      currentDefenderStore.setCurrentDefender(null)

      // Copy legacy profiles to new object.
      const profilesLegacy = cloneDeep(globalStore.profiles)
      const time = timestamp()
      const migratedProfiles = []

      // Iterate through legacy attacker profiles.
      profilesLegacy.attackers.forEach((profile) => {
        // Add missing properties.
        profile.roles = ["Attacker"]
        profile.ignore = null
        profile.invuln = null
        profile.keywords = []
        profile.modelCount = null
        profile.save = null
        profile.toughness = null
        profile.wounds = null
        profile.created = time
        profile.updated = profile.created
        // Push to migratedProfiles array.
        migratedProfiles.push(profile)
      })

      // Iterate through legacy attacker profiles.
      profilesLegacy.defenders.forEach((profile) => {
        // Add missing properties.
        profile.roles = ["Defender"]
        profile.bs = null
        profile.points = null
        profile.weapons = []
        profile.created = time
        profile.updated = profile.created
        if (profile.ignore === false) {
          profile.ignore = null
        }
        if (profile.invuln === false) {
          profile.invuln = null
        }
        // Push to migratedProfiles array.
        migratedProfiles.push(profile)
      })

      // Convert store profiles from an object of 2 arrays (attacker/defender)
      // to just a single flat array of profiles.
      globalStore.setProfiles(migratedProfiles)
      console.log("Profiles migrated.")
    }

    function updateProfiles(
      options: {
        applyAllUpdates?: boolean
        applyLatestUpdate?: boolean
        versionToUpdateFrom?: string
      } = {}
    ) {
      // Applies any pending updates to profile data.

      const {
        applyAllUpdates = false,
        applyLatestUpdate = false,
        versionToUpdateFrom,
      } = options

      console.log("Updating profiles...")
      const globalStore = useGlobalStore()
      globalStore.readOnlyProfiles = true

      checkDataIntegrity()

      // Loop through profiles
      globalStore.profiles.forEach((profile, profileIndex) => {
        console.log(
          `Processing profile ${profileIndex}: "${profile?.name || "Unknown"}"`
        )

        // Additional safety check before processing
        if (!profile || typeof profile !== "object") {
          console.error(`❌ Invalid profile at index ${profileIndex}:`, profile)
          throw new Error(`Invalid profile at index ${profileIndex}`)
        }

        if (!profile.name || typeof profile.name !== "string") {
          console.error(
            `❌ Profile at index ${profileIndex} has invalid name:`,
            profile.name
          )
          throw new Error(`Profile at index ${profileIndex} has invalid name`)
        }

        const profileProcessed = updateProfile(profile, {
          applyAllUpdates,
          applyLatestUpdate,
          versionToUpdateFrom,
        })

        console.log(
          `Completed processing profile: "${profileProcessed?.name || "Unknown"}"`
        )

        // Check if the current profile was updated.
        let setAsCurrent = null

        if (profileProcessed.updateStatus.updated) {
          // Check if the updated profile was set as the current attacker or defender.
          const currentAttackerStore = useCurrentAttackerStore()
          const currentDefenderStore = useCurrentDefenderStore()
          if (typeof currentAttackerStore.currentAttacker.id !== "undefined") {
            if (
              profileProcessed.id === currentAttackerStore.currentAttacker.id
            ) {
              setAsCurrent = "Attacker"
            }
          }
          if (typeof currentDefenderStore.currentDefender.id !== "undefined") {
            if (
              profileProcessed.id === currentDefenderStore.currentDefender.id
            ) {
              setAsCurrent = "Defender"
            }
          }

          // We no longer need the updateStatus data, delete the property.
          delete profileProcessed.updateStatus

          // Write the updated profile data.
          globalStore.writeProfile({
            isClone: false,
            index: profileIndex,
            profile: profileProcessed,
            setAsCurrent,
          })
        }
      })

      // UX nicety.
      // If there is a current attacker set...
      const currentAttackerStore = useCurrentAttackerStore()
      if (typeof currentAttackerStore.currentAttacker.id !== "undefined") {
        // and that attacker doesn't have the "Attacker: Melee" role...
        if (
          !currentAttackerStore.currentAttacker.computed_roles.includes(
            "Attacker: Melee"
          )
        ) {
          // set the attack type to "Ranged".
          const attackContextStore = useAttackContextStore()
          attackContextStore.attackContext.attackType = "Ranged"
        }
      }

      globalStore.readOnlyProfiles = false
      console.log("Profiles updated.")
    }

    function updateLastSeen() {
      lastSeen.value = Date.now()
    }

    return {
      // State
      lastSeen,
      updateInProgress,
      versionToUpdateFrom,
      // Actions
      checkDataIntegrity,
      migrateProfiles,
      updateProfiles,
      updateLastSeen,
    }
  },
  {
    persist: {
      key: "unitcrunch-update-management",
      storage: piniaPluginPersistedstate.localStorage(),
      pick: ["lastSeen"],
    },
  }
)
